<script setup>
import { computed, onMounted, ref, watch } from 'vue';
import { useRoute, useRouter } from 'vue-router';
import { useI18n } from 'vue-i18n';
import Button from 'dashboard/components-next/button/Button.vue';
import { useZuwadAssistantStore } from 'dashboard/stores/zuwadAssistant';
import AssistantChat from 'dashboard/routes/dashboard/zuwadAssistant/components/AssistantChat.vue';

// Zuwad AI on every dashboard page: a bubble that opens a small chat with the latest thread.
const route = useRoute();
const router = useRouter();
const { t } = useI18n();
const store = useZuwadAssistantStore();
const open = ref(false);

const CONVERSATION_ROUTES = [
  'inbox_conversation',
  'conversation_through_inbox',
  'conversations_through_label',
  'team_conversations_through_label',
  'conversations_through_folders',
  'conversation_through_mentions',
  'conversation_through_unattended',
  'conversation_through_participating',
  'inbox_view_conversation',
];
const onAssistantPage = computed(() =>
  String(route.name || '').startsWith('zuwad_assistant')
);
// On a conversation the reply box fills the bottom, so the bubble sits under the header instead.
const onConversation = computed(() => CONVERSATION_ROUTES.includes(route.name));
const accountId = computed(() => Number(route.params.accountId));

// The badge needs the thread list on every page, not only after the bubble opens.
const loadForBadge = () => {
  if (accountId.value && (!store.loaded || store.accountId !== accountId.value))
    store.load(accountId.value).catch(() => {});
};
onMounted(loadForBadge);
watch(accountId, loadForBadge);
watch(
  () => store.requestedThreadId,
  threadId => {
    if (!threadId) return;
    store.requestedThreadId = null;
    open.value = false;
    router.push({
      name: 'zuwad_assistant_thread',
      params: { accountId: accountId.value, threadId },
    });
  }
);

const toggle = async () => {
  open.value = !open.value;
  if (!open.value) return;
  if (!store.loaded || store.accountId !== accountId.value)
    await store.load(accountId.value);
  // The bubble opens the user's own latest chat, not a teammate's.
  const latestOwn = store.threads.find(thread => thread.mine !== false);
  if (!store.activeThreadId && latestOwn) await store.open(latestOwn.id);
  else if (store.activeThreadId) await store.open(store.activeThreadId);
};
const newChat = () => store.open(null);
const openPage = () => {
  open.value = false;
  router.push(
    store.activeThreadId
      ? {
          name: 'zuwad_assistant_thread',
          params: {
            accountId: accountId.value,
            threadId: store.activeThreadId,
          },
        }
      : { name: 'zuwad_assistant', params: { accountId: accountId.value } }
  );
};
</script>

<template>
  <template v-if="!onAssistantPage && accountId">
    <div
      v-if="open"
      class="fixed z-50 flex flex-col overflow-hidden rounded-2xl border border-n-weak bg-n-solid-1 shadow-xl ltr:right-4 rtl:left-4 bottom-4 w-[min(400px,calc(100vw-2rem))] h-[min(600px,calc(100vh-6rem))]"
    >
      <header class="flex items-center gap-1 px-3 py-2 border-b border-n-weak">
        <span
          class="flex items-center justify-center rounded-md size-6 bg-n-brand text-n-gold"
        >
          <span class="i-woot-zuwad-ai size-4" />
        </span>
        <span
          dir="auto"
          class="flex-1 truncate text-sm font-medium text-n-slate-12"
        >
          {{ store.activeThread?.title || t('ZUWAD_ASSISTANT.TITLE') }}
        </span>
        <Button
          icon="i-lucide-plus"
          variant="ghost"
          color="slate"
          size="xs"
          :aria-label="t('ZUWAD_ASSISTANT.NEW_CHAT')"
          @click="newChat"
        />
        <Button
          icon="i-lucide-maximize-2"
          variant="ghost"
          color="slate"
          size="xs"
          :aria-label="t('ZUWAD_ASSISTANT.OPEN_PAGE')"
          @click="openPage"
        />
        <Button
          icon="i-lucide-x"
          variant="ghost"
          color="slate"
          size="xs"
          :aria-label="t('ZUWAD_ASSISTANT.CLOSE')"
          @click="open = false"
        />
      </header>
      <AssistantChat compact class="flex-1 min-h-0" />
    </div>
    <button
      v-else
      type="button"
      class="fixed z-40 flex items-center justify-center rounded-full shadow-lg size-12 bg-n-brand text-n-gold ring-2 ring-n-gold/40 transition hover:scale-105 hover:brightness-110 ltr:right-4 rtl:left-4"
      :class="onConversation ? 'top-[4.5rem]' : 'bottom-4'"
      :aria-label="t('ZUWAD_ASSISTANT.OPEN')"
      :title="t('ZUWAD_ASSISTANT.OPEN')"
      @click="toggle"
    >
      <span class="i-woot-zuwad-ai size-6" />
      <span
        v-if="store.unreadTotal"
        class="absolute -top-1 ltr:-right-1 rtl:-left-1 inline-grid h-5 min-w-5 place-items-center rounded-full bg-n-ruby-9 px-1 text-xxs font-medium text-white"
      >
        {{ store.unreadTotal > 9 ? '9+' : store.unreadTotal }}
      </span>
      <span
        v-else-if="store.isAnswering"
        class="absolute top-0 ltr:right-0 rtl:left-0 rounded-full size-3 bg-n-amber-9 animate-pulse"
      />
    </button>
  </template>
</template>
